import { generatePath } from 'react-router-dom';

import { DetailsTabs } from '@AdminV2/components/underwriting/underwriting-details/tab-keys.ts';
import { adminV2AppRoot } from '@/constants.ts';

export const ADMIN_ROUTES = {
    ADMIN_ROOT: adminV2AppRoot,
    DASHBOARD: adminV2AppRoot,
    APPLICATIONS: 'applications',
    /** @deprecated the path only redirects: the deal request is a tab set of its application */
    DEAL_REQUESTS: 'deal-requests',
    /** the retired deal-requests list and details, kept reachable */
    DEAL_REQUESTS_DEPRECATED: 'deal-requests-deprecated',
    UNDERWRITING: 'underwriting',
    UNDERWRITING_REPORT: 'report',
    /** @deprecated the paths only redirect: the offer is a tab of its underwriting */
    OFFERS: 'offers',
    /** the retired offers list and details, kept reachable */
    OFFERS_DEPRECATED: 'offers-deprecated',
    CLOSING: 'closing',
    SERVICING_HUB: 'servicing-hub',
    INVESTORS: 'investors',
    DEALS: 'deals',
    BUSINESSES: 'businesses',
    BUSINESS_OWNERS: 'business-owners',
    /** @deprecated the paths only redirect: the pre-qual is a tab of its application */
    PREQUAL: 'pre-qualification',
    /** the retired pre-qualification list and details, kept reachable */
    PREQUAL_DEPRECATED: 'pre-qualification-deprecated',
    APPROVALS: 'approvals',
    ALL_TRANSACTIONS: 'all-transactions',
    CRS_REPORTS: 'crs-reports',
    DEAL_REVENUE: 'deal-revenue',
    ACTION_LOG: 'action-log',
    NICKS_REPORT: 'nicks-report',
    IRR_REPORTING: 'irr-reporting',
    EMPLOYEES: 'employees',
    SPVS: 'spvs',
    FUNDS: 'funds',
    SETTINGS: 'settings',
    PROFILE: 'profile',

    NEW: 'new',
    DETAILS: ':id',
    REVENUE_READING: 'revenue/:month',

    SERVICING_RUNS: 'runs',
    SERVICING_RUN: 'runs/:runId',
    SERVICING_RECORD: 'records/:recordId',
} as const;
export type ADMIN_ROUTES = (typeof ADMIN_ROUTES)[keyof typeof ADMIN_ROUTES];

export function path<
    PathPart extends Exclude<ADMIN_ROUTES, typeof ADMIN_ROUTES.ADMIN_ROOT>,
    Params extends Record<string, string | number>,
>(...paths: [...PathPart[], Params] | PathPart[]) {
    let pathParts: PathPart[] = [];
    let params: Params | null = null;

    if (paths.length > 1 && typeof paths.at(-1) === 'object') {
        params = paths.at(-1) as Params;
        pathParts = paths.slice(0, -1) as PathPart[];
    } else {
        pathParts = paths.slice() as PathPart[];
    }
    const fullpath = [ADMIN_ROUTES.ADMIN_ROOT, ...pathParts].join('/');

    return params ? generatePath(fullpath, params) : fullpath;
}

/** @deprecated use `applicationDetailsPath`: the deal request is a tab set of its application */
export const dealRequestDetailsPath = (id: number) =>
    path(ADMIN_ROUTES.DEAL_REQUESTS_DEPRECATED, ADMIN_ROUTES.DETAILS, { id });

export const applicationDetailsPath = (id: number) =>
    path(ADMIN_ROUTES.APPLICATIONS, ADMIN_ROUTES.DETAILS, { id });

export const underwritingDetailsPath = (id: number) =>
    path(ADMIN_ROUTES.UNDERWRITING, ADMIN_ROUTES.DETAILS, { id });

export const underwritingReportPath = (id: number) =>
    path(ADMIN_ROUTES.UNDERWRITING, ADMIN_ROUTES.DETAILS, ADMIN_ROUTES.UNDERWRITING_REPORT, { id });

/** the offer is a tab of its underwriting, not a page of its own */
export const underwritingOfferTabPath = (underwritingId: number) =>
    `${underwritingDetailsPath(underwritingId)}#${DetailsTabs.offer}`;

/** Query key carrying the checklist item a report drill-down came from, so it can scroll back to it */
export const CHECKLIST_ITEM_PARAM = 'item';

/** @deprecated the offer lives on the underwriting Offer tab - use `underwritingOfferTabPath` */
export const offerDetailsPath = (id: number) =>
    path(ADMIN_ROUTES.OFFERS_DEPRECATED, ADMIN_ROUTES.DETAILS, { id });

export const closingDetailsPath = (id: number) =>
    path(ADMIN_ROUTES.CLOSING, ADMIN_ROUTES.DETAILS, { id });

export const dealDetailsPath = (id: number) =>
    path(ADMIN_ROUTES.DEALS, ADMIN_ROUTES.DETAILS, { id });

export const spvDetailsPath = (id: number) => path(ADMIN_ROUTES.SPVS, ADMIN_ROUTES.DETAILS, { id });
export const fundDetailsPath = (id: number) =>
    path(ADMIN_ROUTES.FUNDS, ADMIN_ROUTES.DETAILS, { id });

export const businessDetailsPath = (id: number) =>
    path(ADMIN_ROUTES.BUSINESSES, ADMIN_ROUTES.DETAILS, { id });

export const investorDetailsPath = (id: Auth0UserId) =>
    path(ADMIN_ROUTES.INVESTORS, ADMIN_ROUTES.DETAILS, { id });

export const ownerDetailsPath = (id: Auth0UserId) =>
    path(ADMIN_ROUTES.BUSINESS_OWNERS, ADMIN_ROUTES.DETAILS, { id });

export const revenueReadingPath = (id: number, month: string) =>
    path(ADMIN_ROUTES.DEALS, ADMIN_ROUTES.DETAILS, ADMIN_ROUTES.REVENUE_READING, { id, month });

export const servicingRunPath = (runId: number) =>
    path(ADMIN_ROUTES.SERVICING_HUB, ADMIN_ROUTES.SERVICING_RUN, { runId });

export const servicingRecordPath = (recordId: number) =>
    path(ADMIN_ROUTES.SERVICING_HUB, ADMIN_ROUTES.SERVICING_RECORD, { recordId });

export const irrReportingPath = () => path(ADMIN_ROUTES.IRR_REPORTING);

export const irrReportingDetailsPath = (id: number) =>
    path(ADMIN_ROUTES.IRR_REPORTING, ADMIN_ROUTES.DETAILS, { id });
